import { createContext, lazy, Suspense, useContext, useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { api } from './lib/api'
import type { Me } from './lib/api'

// Route-level code splitting (P2): each page is its own chunk so the initial
// bundle stays small; the charts-heavy pages load only when visited.
const Dashboard = lazy(() => import('./pages/Dashboard'))
const DiscoverPage = lazy(() => import('./pages/DiscoverPage'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const VerifyPage = lazy(() => import('./pages/VerifyPage'))
const SignalsPage = lazy(() => import('./pages/SignalsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const CompanyProfilePage = lazy(() => import('./pages/CompanyProfilePage'))
const WatchlistsPage = lazy(() => import('./pages/WatchlistsPage'))
const WebhooksPage = lazy(() => import('./pages/WebhooksPage'))
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'))
const LegalPage = lazy(() => import('./pages/LegalPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const TeamPage = lazy(() => import('./pages/TeamPage'))
const ChannelsPage = lazy(() => import('./pages/ChannelsPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const PlaygroundPage = lazy(() => import('./pages/PlaygroundPage'))
const CasesPage = lazy(() => import('./pages/CasesPage'))

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" role="status" aria-label="Loading">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
    </div>
  )
}

interface MeState {
  me: Me | null
  loading: boolean
  refresh: () => void
}

const MeContext = createContext<MeState>({ me: null, loading: true, refresh: () => {} })

export function useMe() {
  return useContext(MeContext)
}

export default function App() {
  const [state, setState] = useState<MeState>({ me: null, loading: true, refresh: () => {} })

  const load = () => {
    setState((prev) => ({ ...prev, loading: true }))
    api
      .me()
      .then((me) => setState({ me, loading: false, refresh: load }))
      .catch(() =>
        setState({
          me: {
            tenantId: '',
            email: '',
            roles: ['ANALYST'],
            scopes: ['companies:read', 'verify:write', 'signals:read'],
            authType: 'api-key',
          },
          loading: false,
          refresh: load,
        }),
      )
  }

  useEffect(load, [])

  if (state.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--surface)]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <MeContext.Provider value={state}>
      <BrowserRouter>
        <AppShell me={state.me}>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/verify" element={<VerifyPage />} />
            <Route path="/signals" element={<SignalsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/keys" element={<ApiKeysPage />} />
            <Route path="/companies/:recordId" element={<CompanyProfilePage />} />
            <Route path="/watchlists" element={<WatchlistsPage />} />
            <Route path="/webhooks" element={<WebhooksPage />} />
            <Route path="/cases" element={<CasesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/playground" element={<PlaygroundPage />} />
            <Route
              path="/team"
              element={
                state.me?.roles.some((r) => ['TENANT_ADMIN', 'PLATFORM_ADMIN'].includes(r)) ? (
                  <TeamPage />
                ) : (
                  <div className="p-8 text-sm text-[var(--text-muted)]">Tenant admins only.</div>
                )
              }
            />
            <Route
              path="/channels"
              element={
                state.me?.roles.some((r) => ['TENANT_ADMIN', 'PLATFORM_ADMIN'].includes(r)) ? (
                  <ChannelsPage />
                ) : (
                  <div className="p-8 text-sm text-[var(--text-muted)]">Tenant admins only.</div>
                )
              }
            />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route
              path="/admin"
              element={
                state.me?.roles.includes('PLATFORM_ADMIN') ? (
                  <AdminPage />
                ) : (
                  <div className="p-8 text-sm text-[var(--text-muted)]">Platform admins only.</div>
                )
              }
            />
            <Route path="*" element={<div className="p-8 text-sm text-[var(--text-muted)]">Not found</div>} />
            </Routes>
          </Suspense>
        </AppShell>
      </BrowserRouter>
    </MeContext.Provider>
  )
}
